import React, { useEffect, useMemo, useState } from 'react';
import './styles.scss';

interface Post {
  id: number;
  title: string;
  text: string;
  authorName: string;
  at: number;
  canRemove: boolean;
}

interface Note {
  id: string;
  title: string;
  text: string;
  author: string;
  at: number;
  origin?: string;
}

interface State {
  board: { id: string; name: string } | null;
  posts: Post[];
  notes: Note[];
  nearby: string[];
}

type Tab = 'board' | 'notes';

const MAX_TITLE = 60;
const MAX_TEXT = 600;

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ panel: 'noticeBoard', ...payload }));
  } catch (e) {
    // Outside the game (a browser preview): nothing to send to
    // eslint-disable-next-line no-console
    console.log('noticeBoard sendMessage', payload);
  }
};

function formatDate(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// A small fixed tilt per paper, so the board looks hand-pinned and never reshuffles between two renderings
function tiltFor(key: string | number): number {
  const s = String(key);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return ((Math.abs(h) % 7) - 3) * 0.7;
}

const NoticeBoard: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<State>({ board: null, posts: [], notes: [], nearby: [] });
  const [tab, setTab] = useState<Tab>('board');
  const [openId, setOpenId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [giving, setGiving] = useState<string | null>(null);

  useEffect(() => {
    const showHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as Partial<State> | null;
      const next: State = {
        board: detail?.board ?? null,
        posts: detail?.posts ?? [],
        notes: detail?.notes ?? [],
        nearby: detail?.nearby ?? [],
      };
      setState(next);
      setVisible((wasVisible) => {
        // Freshly opened: the board when there is one, else the carnet
        if (!wasVisible) {
          setTab(next.board ? 'board' : 'notes');
          setOpenId(null);
          setGiving(null);
        }
        return true;
      });
      if (!next.board) setTab('notes');
    };
    const hideHandler = () => {
      setVisible(false);
      setGiving(null);
    };
    window.addEventListener('skymp5-client:showNoticeBoard', showHandler);
    window.addEventListener('skymp5-client:hideNoticeBoard', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showNoticeBoard', showHandler);
      window.removeEventListener('skymp5-client:hideNoticeBoard', hideHandler);
    };
  }, []);

  const sortedNotes = useMemo(() => [...state.notes].sort((a, b) => b.at - a.at), [state.notes]);

  if (!visible) return null;

  const close = () => {
    setVisible(false);
    send({ action: 'close' });
  };

  const write = () => {
    if (!title.trim() || !text.trim()) return;
    send({ action: 'write', title: title.trim(), text: text.trim() });
    setTitle('');
    setText('');
  };

  const hasBoard = state.board !== null;

  return (
    <div className="nb-overlay">
      <div className="nb-frame" role="dialog" aria-label="Tableau d'affichage">
        <div className="nb-header">
          <div className="nb-title">{hasBoard ? state.board?.name : 'Carnet de notes'}</div>
          <div className="nb-tabs" role="tablist">
            {hasBoard && (
              <button type="button" role="tab" aria-selected={tab === 'board'} className={`nb-tab${tab === 'board' ? ' nb-tab--on' : ''}`} onClick={() => setTab('board')}>
                Le tableau
              </button>
            )}
            <button type="button" role="tab" aria-selected={tab === 'notes'} className={`nb-tab${tab === 'notes' ? ' nb-tab--on' : ''}`} onClick={() => setTab('notes')}>
              Mon carnet ({state.notes.length})
            </button>
          </div>
          <button type="button" className="nb-close" onClick={close}>
            Fermer
          </button>
        </div>

        {tab === 'board' && hasBoard && (
          <div className="nb-board">
            {state.posts.length === 0 ? (
              <div className="nb-empty">
                Le tableau est vide. Écris une note dans ton carnet, puis épingle-la ici.
              </div>
            ) : (
              <div className="nb-grid">
                {state.posts.map((p) => {
                  const open = openId === `p${p.id}`;
                  return (
                    <div key={p.id} className={`nb-paper${open ? ' nb-paper--open' : ''}`} style={{ transform: open ? 'none' : `rotate(${tiltFor(p.id)}deg)` }}>
                      <span className="nb-pin" aria-hidden="true" />
                      <button type="button" className="nb-paper-body" onClick={() => setOpenId(open ? null : `p${p.id}`)} aria-expanded={open}>
                        <span className="nb-paper-title">{p.title}</span>
                        <span className={`nb-paper-text${open ? '' : ' nb-paper-text--clamp'}`}>{p.text}</span>
                        <span className="nb-paper-meta">
                          {p.authorName}, {formatDate(p.at)}
                        </span>
                      </button>
                      <div className="nb-paper-actions">
                        <button type="button" className="nb-btn" onClick={() => send({ action: 'tearOff', postId: p.id })} title="Une copie pour ton carnet, la note reste sur le tableau">
                          Arracher une copie
                        </button>
                        {p.canRemove && (
                          <button type="button" className="nb-btn nb-btn--quiet" onClick={() => send({ action: 'remove', postId: p.id })}>
                            Retirer
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'notes' && (
          <div className="nb-notes">
            <div className="nb-editor">
              <div className="nb-editor-title">Écrire une note</div>
              <input className="nb-input" type="text" value={title} maxLength={MAX_TITLE} placeholder="Titre" onChange={(e) => setTitle(e.target.value)} />
              <textarea className="nb-textarea" value={text} maxLength={MAX_TEXT} placeholder="Ce que tu veux écrire..." onChange={(e) => setText(e.target.value)} />
              <div className="nb-editor-foot">
                <span className="nb-count">
                  {text.length}/{MAX_TEXT}
                </span>
                <button type="button" className="nb-btn nb-btn--main" disabled={!title.trim() || !text.trim()} onClick={write}>
                  Ajouter au carnet
                </button>
              </div>
            </div>

            <div className="nb-list">
              {sortedNotes.length === 0 ? (
                <div className="nb-empty">Ton carnet est vide.</div>
              ) : (
                sortedNotes.map((n) => {
                  const open = openId === n.id;
                  return (
                    <div key={n.id} className={`nb-paper nb-paper--list${open ? ' nb-paper--open' : ''}`}>
                      <button type="button" className="nb-paper-body" onClick={() => setOpenId(open ? null : n.id)} aria-expanded={open}>
                        <span className="nb-paper-title">{n.title}</span>
                        <span className={`nb-paper-text${open ? '' : ' nb-paper-text--clamp'}`}>{n.text}</span>
                        <span className="nb-paper-meta">
                          {n.author}, {formatDate(n.at)}
                          {n.origin ? ` (${n.origin})` : ''}
                        </span>
                      </button>
                      <div className="nb-paper-actions">
                        {hasBoard && (
                          <button type="button" className="nb-btn nb-btn--main" onClick={() => send({ action: 'pin', noteId: n.id })}>
                            Épingler ici
                          </button>
                        )}
                        <button type="button" className="nb-btn" onClick={() => setGiving(giving === n.id ? null : n.id)}>
                          Donner
                        </button>
                        <button type="button" className="nb-btn nb-btn--quiet" onClick={() => send({ action: 'delete', id: n.id })}>
                          Jeter
                        </button>
                      </div>
                      {giving === n.id && (
                        <div className="nb-give">
                          {state.nearby.length === 0 ? (
                            <span className="nb-give-none">Personne n'est assez près de toi.</span>
                          ) : (
                            state.nearby.map((name) => (
                              <button
                                key={name}
                                type="button"
                                className="nb-btn"
                                onClick={() => {
                                  send({ action: 'give', id: n.id, toName: name });
                                  setGiving(null);
                                }}
                              >
                                {name}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        <div className="nb-foot">
          <kbd>Échap</kbd> Fermer
        </div>
      </div>
    </div>
  );
};

export default NoticeBoard;
